import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { type OfficeAgent, type OfficeChatMessage, type OfficeZone, type AgentExecEvent } from "../../hooks/useOfficeWS.js";
import {
  Building2, Users, Activity, AlertTriangle,
  MessageSquare, Send, Clock, Zap, Brain,
  Code, Megaphone, Shield, Settings2,
  X, Loader2, CheckCircle2, Coffee, Sparkles,
  Hash, WifiOff, Play, Square, Terminal,
  Globe, FileCode, ChevronDown, ChevronUp
} from "lucide-react";

// ─── Zone Config ────────────────────────────────────────────────

const ZONE_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; icon: typeof Brain }> = {
  executive:    { label: "Executivo",    color: "text-amber-400",   bg: "bg-amber-500/8",   border: "border-amber-500/20", icon: Brain },
  marketing:    { label: "Marketing",    color: "text-blue-400",    bg: "bg-blue-500/8",    border: "border-blue-500/20",  icon: Megaphone },
  content:      { label: "Conteúdo",     color: "text-purple-400",  bg: "bg-purple-500/8",  border: "border-purple-500/20", icon: Sparkles },
  engineering:  { label: "Engenharia",   color: "text-green-400",   bg: "bg-green-500/8",   border: "border-green-500/20", icon: Code },
  operations:   { label: "Operações",    color: "text-cyan-400",    bg: "bg-cyan-500/8",    border: "border-cyan-500/20",  icon: Settings2 },
  quality:      { label: "Qualidade",    color: "text-rose-400",    bg: "bg-rose-500/8",    border: "border-rose-500/20",  icon: Shield },
  unassigned:   { label: "Sem Zona",     color: "text-muted-foreground", bg: "bg-muted/30", border: "border-border",       icon: Users },
};

const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string; icon: typeof Activity }> = {
  idle:      { label: "Disponível",  color: "text-muted-foreground", dot: "bg-muted-foreground",         icon: Coffee },
  working:   { label: "Trabalhando", color: "text-primary",          dot: "bg-primary animate-pulse",    icon: Activity },
  thinking:  { label: "Pensando",    color: "text-amber-400",        dot: "bg-amber-400 animate-pulse",  icon: Brain },
  speaking:  { label: "Falando",     color: "text-blue-400",         dot: "bg-blue-400 animate-pulse",   icon: MessageSquare },
  error:     { label: "Erro",        color: "text-red-400",          dot: "bg-red-400 animate-pulse",    icon: AlertTriangle },
  offline:   { label: "Offline",     color: "text-muted-foreground", dot: "bg-muted-foreground/50",      icon: WifiOff },
};

const TOOL_ICONS: Record<string, typeof Terminal> = {
  shell: Terminal,
  browser: Globe,
  code: FileCode,
  sandbox: Terminal,
};

// ─── Agent Avatar Component ─────────────────────────────────────

function AgentAvatar({
  agent,
  isSelected,
  onClick,
}: {
  agent: OfficeAgent;
  isSelected: boolean;
  onClick: () => void;
}) {
  const status = STATUS_CONFIG[agent.status] || STATUS_CONFIG.idle;
  const zone = ZONE_CONFIG[agent.zone] || ZONE_CONFIG.unassigned;
  const initials = agent.display_name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <button
      onClick={onClick}
      className={`group relative flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200 cursor-pointer
        ${isSelected
          ? "bg-primary/10 border border-primary/30 shadow-lg shadow-primary/5 scale-105"
          : "hover:bg-card/80 hover:shadow-md border border-transparent"
        }`}
      title={`${agent.display_name} — ${status.label}`}
    >
      <div className={`relative w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold
        ${zone.bg} ${zone.border} border ${zone.color}`}>
        {initials}
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background ${status.dot}`} />
      </div>
      <span className="text-[10px] font-medium text-muted-foreground truncate max-w-[72px] leading-tight text-center">
        {agent.display_name.split(" ").slice(0, 2).join(" ")}
      </span>
      {agent.status === "working" && agent.current_tool && (
        <span className="absolute -top-1 -right-1 px-1 py-0.5 bg-primary/20 text-primary text-[8px] font-mono rounded-md border border-primary/20 truncate max-w-[60px]">
          {agent.current_tool}
        </span>
      )}
      {agent.last_message && agent.status !== "idle" && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-card border border-border rounded-lg shadow-lg text-[9px] text-foreground max-w-[120px] truncate whitespace-nowrap z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          {agent.last_message}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-card border-r border-b border-border rotate-45" />
        </div>
      )}
    </button>
  );
}

// ─── Zone Section Component ─────────────────────────────────────

function ZoneSection({
  zoneName,
  agents,
  selectedAgent,
  onSelectAgent,
}: {
  zoneName: string;
  agents: OfficeAgent[];
  selectedAgent: string | null;
  onSelectAgent: (id: string) => void;
}) {
  const zone = ZONE_CONFIG[zoneName] || ZONE_CONFIG.unassigned;
  const ZoneIcon = zone.icon;
  const activeCount = agents.filter((a) => ["working", "thinking", "speaking"].includes(a.status)).length;

  return (
    <div className={`rounded-xl ${zone.bg} ${zone.border} border p-3`}>
      <div className="flex items-center gap-2 mb-2">
        <ZoneIcon className={`w-3.5 h-3.5 ${zone.color}`} />
        <span className={`text-xs font-semibold ${zone.color}`}>{zone.label}</span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {activeCount > 0 && <span className="text-primary">{activeCount} ativo{activeCount > 1 ? "s" : ""}</span>}
          {activeCount === 0 && `${agents.length} agente${agents.length > 1 ? "s" : ""}`}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {agents.map((agent) => (
          <AgentAvatar
            key={agent.agent_id}
            agent={agent}
            isSelected={selectedAgent === agent.agent_id}
            onClick={() => onSelectAgent(agent.agent_id)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Agent Execution Log ────────────────────────────────────────

function AgentExecutionLog({ events }: { events: AgentExecEvent[] }) {
  const logRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (logRef.current && expanded) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events, expanded]);

  if (events.length === 0) return null;

  const getEventIcon = (type: string) => {
    switch (type) {
      case "thinking": case "thought": return <Brain className="w-3 h-3 text-amber-400" />;
      case "tool_start": return <Zap className="w-3 h-3 text-primary" />;
      case "tool_result": return <CheckCircle2 className="w-3 h-3 text-green-400" />;
      case "error": return <AlertTriangle className="w-3 h-3 text-red-400" />;
      case "plan": return <Settings2 className="w-3 h-3 text-cyan-400" />;
      case "done": return <CheckCircle2 className="w-3 h-3 text-green-400" />;
      default: return <Activity className="w-3 h-3 text-muted-foreground" />;
    }
  };

  const getEventLabel = (ev: AgentExecEvent) => {
    switch (ev.type) {
      case "started": return `Iniciando tarefa...`;
      case "thinking": return `Pensando...`;
      case "thought": return ev.content?.slice(0, 120) || "Raciocínio interno";
      case "plan": return ev.content?.slice(0, 120) || "Planejando ação";
      case "tool_start": return `${ev.connector ? ev.connector + "." : ""}${ev.tool || "tool"}`;
      case "tool_result": return ev.content?.slice(0, 100) || "Resultado recebido";
      case "response": return ev.content?.slice(0, 150) || "Resposta";
      case "error": return ev.content || "Erro";
      case "done": return "Tarefa concluída";
      default: return ev.type;
    }
  };

  return (
    <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/30 transition-colors"
      >
        <Terminal className="w-3.5 h-3.5 text-primary" />
        <span className="text-[11px] font-semibold text-foreground">Log de Execução</span>
        <span className="text-[10px] text-muted-foreground ml-1">({events.length} eventos)</span>
        {expanded ? <ChevronUp className="w-3 h-3 ml-auto text-muted-foreground" /> : <ChevronDown className="w-3 h-3 ml-auto text-muted-foreground" />}
      </button>
      {expanded && (
        <div ref={logRef} className="max-h-48 overflow-y-auto px-3 pb-2 space-y-1">
          {events.map((ev, i) => (
            <div key={i} className="flex items-start gap-2 py-0.5">
              <div className="mt-0.5 shrink-0">{getEventIcon(ev.type)}</div>
              <div className="flex-1 min-w-0">
                <span className="text-[10px] font-mono text-muted-foreground">{ev.type}</span>
                <p className="text-[11px] text-foreground/80 break-words leading-tight">{getEventLabel(ev)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Agent Detail Panel (with Execute) ──────────────────────────

function AgentDetailPanel({
  agent,
  onClose,
  onExecute,
  isExecuting,
  execEvents,
}: {
  agent: OfficeAgent;
  onClose: () => void;
  onExecute: (task: string) => void;
  isExecuting: boolean;
  execEvents: AgentExecEvent[];
}) {
  const [taskInput, setTaskInput] = useState("");
  const [showTaskInput, setShowTaskInput] = useState(false);
  const status = STATUS_CONFIG[agent.status] || STATUS_CONFIG.idle;
  const zone = ZONE_CONFIG[agent.zone] || ZONE_CONFIG.unassigned;
  const lastActive = agent.last_activity
    ? new Date(agent.last_activity * 1000).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    : "—";

  const handleExecute = () => {
    if (!taskInput.trim()) return;
    onExecute(taskInput.trim());
    setTaskInput("");
    setShowTaskInput(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold ${zone.bg} ${zone.border} border ${zone.color}`}>
            {agent.display_name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{agent.display_name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
              <span className={`text-xs ${status.color}`}>{status.label}</span>
              <span className="text-muted-foreground text-[10px]">•</span>
              <span className={`text-xs ${zone.color}`}>{zone.label}</span>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-muted/50 text-muted-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-muted/30 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-foreground">{agent.tasks_completed_today}</div>
          <div className="text-[10px] text-muted-foreground">Tarefas Hoje</div>
        </div>
        <div className="bg-muted/30 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-foreground">{agent.error_count}</div>
          <div className="text-[10px] text-muted-foreground">Erros</div>
        </div>
        <div className="bg-muted/30 rounded-lg p-2 text-center">
          <div className="text-xs font-mono font-bold text-foreground">{lastActive}</div>
          <div className="text-[10px] text-muted-foreground">Última Ativ.</div>
        </div>
      </div>

      {/* Current Task */}
      {agent.current_task && (
        <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
          <div className="text-[10px] font-semibold text-primary uppercase tracking-wider mb-1">Tarefa Atual</div>
          <p className="text-xs text-foreground">{agent.current_task}</p>
          {agent.current_tool && (
            <div className="flex items-center gap-1 mt-1.5">
              <Zap className="w-3 h-3 text-amber-400" />
              <span className="text-[10px] font-mono text-muted-foreground">
                {agent.current_connector && `${agent.current_connector}.`}{agent.current_tool}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Execution Log */}
      <AgentExecutionLog events={execEvents} />

      {/* Execute Task Button / Input */}
      {!isExecuting && !showTaskInput && (
        <button
          onClick={() => setShowTaskInput(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all text-sm font-medium"
        >
          <Play className="w-4 h-4" />
          Atribuir Tarefa
        </button>
      )}

      {showTaskInput && !isExecuting && (
        <div className="space-y-2">
          <textarea
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleExecute();
              }
            }}
            placeholder={`Descreva a tarefa para ${agent.display_name}...`}
            className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
            rows={3}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={handleExecute}
              disabled={!taskInput.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-30 transition-colors text-xs font-medium"
            >
              <Play className="w-3.5 h-3.5" />
              Executar
            </button>
            <button
              onClick={() => { setShowTaskInput(false); setTaskInput(""); }}
              className="px-3 py-2 rounded-lg bg-muted/50 text-muted-foreground hover:bg-muted/80 transition-colors text-xs"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {isExecuting && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs font-medium">Executando tarefa...</span>
        </div>
      )}

      {/* Last Message */}
      {agent.last_message && !isExecuting && execEvents.length === 0 && (
        <div className="bg-muted/20 rounded-lg p-3">
          <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Última Mensagem</div>
          <p className="text-xs text-foreground italic">"{agent.last_message}"</p>
        </div>
      )}
    </div>
  );
}

// ─── Office Chat Panel ──────────────────────────────────────────

// ─── Slash Commands ────────────────────────────────────────────

const SLASH_COMMANDS = [
  { cmd: "/status", description: "Ver status de um agente", usage: "/status [agente]" },
  { cmd: "/assign", description: "Atribuir tarefa a um agente", usage: "/assign [agente] [tarefa]" },
  { cmd: "/execute", description: "Executar tarefa com agente", usage: "/execute [agente] [tarefa]" },
  { cmd: "/agents", description: "Listar todos os agentes", usage: "/agents" },
  { cmd: "/help", description: "Ver comandos disponíveis", usage: "/help" },
];

function OfficeChatPanel({
  messages,
  onSend,
  agents,
  onExecuteAgent,
}: {
  messages: OfficeChatMessage[];
  onSend: (content: string, channel: string) => void;
  agents: Record<string, OfficeAgent>;
  onExecuteAgent?: (agentId: string, task: string) => void;
}) {
  const [input, setInput] = useState("");
  const [channel, setChannel] = useState("general");
  const [showMentions, setShowMentions] = useState(false);
  const [showCommands, setShowCommands] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [commandFilter, setCommandFilter] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const channels = ["general", "marketing", "engineering", "operations"];

  const agentList = useMemo(() => Object.values(agents), [agents]);

  const filteredMessages = useMemo(
    () => messages.filter((m) => m.channel === channel),
    [messages, channel]
  );

  // Auto-scroll when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [filteredMessages.length]);

  // Filtered agents for @mention autocomplete
  const mentionSuggestions = useMemo(() => {
    if (!showMentions) return [];
    const filter = mentionFilter.toLowerCase();
    return agentList.filter((a) =>
      a.display_name.toLowerCase().includes(filter) ||
      a.agent_id.toLowerCase().includes(filter)
    ).slice(0, 6);
  }, [agentList, mentionFilter, showMentions]);

  // Filtered commands for /command autocomplete
  const commandSuggestions = useMemo(() => {
    if (!showCommands) return [];
    const filter = commandFilter.toLowerCase();
    return SLASH_COMMANDS.filter((c) => c.cmd.includes(filter));
  }, [commandFilter, showCommands]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInput(val);

    // Detect @mention trigger
    const lastAt = val.lastIndexOf("@");
    if (lastAt >= 0 && (lastAt === 0 || val[lastAt - 1] === " ")) {
      const afterAt = val.slice(lastAt + 1);
      if (!afterAt.includes(" ")) {
        setShowMentions(true);
        setMentionFilter(afterAt);
        setShowCommands(false);
        return;
      }
    }
    setShowMentions(false);

    // Detect /command trigger
    if (val.startsWith("/")) {
      const cmd = val.split(" ")[0];
      setShowCommands(true);
      setCommandFilter(cmd);
      setShowMentions(false);
      return;
    }
    setShowCommands(false);
  };

  const insertMention = (agent: OfficeAgent) => {
    const lastAt = input.lastIndexOf("@");
    const before = input.slice(0, lastAt);
    setInput(`${before}@${agent.display_name} `);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const insertCommand = (cmd: string) => {
    setInput(cmd + " ");
    setShowCommands(false);
    inputRef.current?.focus();
  };

  // Parse and execute slash commands
  const handleSend = () => {
    if (!input.trim()) return;
    const trimmed = input.trim();

    // Handle /commands locally
    if (trimmed.startsWith("/")) {
      const parts = trimmed.split(/\s+/);
      const cmd = parts[0].toLowerCase();

      if (cmd === "/help") {
        // Show help as a local system message
        onSend("Comandos disponíveis:\n" + SLASH_COMMANDS.map((c) => `${c.cmd} — ${c.description}`).join("\n"), channel);
        setInput("");
        return;
      }

      if (cmd === "/agents") {
        const list = agentList.map((a) => {
          const st = STATUS_CONFIG[a.status];
          return `${a.display_name} [${st?.label || a.status}]`;
        }).join("\n");
        onSend(`Agentes no escritório:\n${list}`, channel);
        setInput("");
        return;
      }

      if (cmd === "/status" && parts.length > 1) {
        const name = parts.slice(1).join(" ").toLowerCase();
        const agent = agentList.find((a) => a.display_name.toLowerCase().includes(name) || a.agent_id.toLowerCase().includes(name));
        if (agent) {
          const st = STATUS_CONFIG[agent.status];
          onSend(`Status de ${agent.display_name}: ${st?.label || agent.status}${agent.current_task ? ` — ${agent.current_task}` : ""}`, channel);
        } else {
          onSend(`Agente "${parts.slice(1).join(" ")}" não encontrado.`, channel);
        }
        setInput("");
        return;
      }

      if ((cmd === "/execute" || cmd === "/assign") && parts.length > 2) {
        const agentName = parts[1].replace(/^@/, "").toLowerCase();
        const task = parts.slice(2).join(" ");
        const agent = agentList.find((a) => a.display_name.toLowerCase().includes(agentName) || a.agent_id.toLowerCase().includes(agentName));
        if (agent && onExecuteAgent) {
          onExecuteAgent(agent.agent_id, task);
          onSend(`Executando tarefa no ${agent.display_name}: ${task}`, channel);
        } else if (!agent) {
          onSend(`Agente "${parts[1]}" não encontrado.`, channel);
        }
        setInput("");
        return;
      }
    }

    onSend(trimmed, channel);
    setInput("");
  };

  // Render message content with @mention highlighting
  const renderContent = (content: string) => {
    const parts = content.split(/(@\w[\w\s]*?)(?=\s|$)/g);
    return parts.map((part, i) => {
      if (part.startsWith("@")) {
        const name = part.slice(1).trim();
        const mentioned = agentList.find((a) => a.display_name.toLowerCase().includes(name.toLowerCase()));
        if (mentioned) {
          const zone = ZONE_CONFIG[mentioned.zone];
          return (
            <span key={i} className={`font-semibold ${zone?.color || "text-primary"}`}>
              {part}
            </span>
          );
        }
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 p-3 border-b border-border">
        <MessageSquare className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Chat do Escritório</span>
        <div className="flex gap-1 ml-auto">
          {channels.map((ch) => (
            <button
              key={ch}
              onClick={() => setChannel(ch)}
              className={`px-2 py-0.5 rounded-md text-[10px] font-medium transition-colors
                ${channel === ch
                  ? "bg-primary/15 text-primary border border-primary/20"
                  : "text-muted-foreground hover:bg-muted/50"
                }`}
            >
              <Hash className="w-2.5 h-2.5 inline mr-0.5" />
              {ch}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-0">
        {filteredMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <MessageSquare className="w-8 h-8 mb-2 opacity-30" />
            <span className="text-xs">Nenhuma mensagem em #{channel}</span>
            <span className="text-[10px] mt-1 text-muted-foreground/60">Use @ para mencionar agentes ou / para comandos</span>
          </div>
        )}
        {filteredMessages.map((msg) => {
          const senderAgent = agents[msg.sender];
          const senderName = senderAgent?.display_name || (msg.sender === "user" ? "Você" : msg.sender);
          const zone = senderAgent ? ZONE_CONFIG[senderAgent.zone] : null;
          const isUser = msg.sender === "user";
          const time = new Date(msg.timestamp * 1000).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
          return (
            <div key={msg.id} className="flex gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0
                ${isUser ? "bg-primary/20 text-primary" : zone ? `${zone.bg} ${zone.color}` : "bg-muted text-muted-foreground"}`}>
                {isUser ? "EU" : senderName.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-[11px] font-semibold ${isUser ? "text-primary" : zone?.color || "text-foreground"}`}>{senderName}</span>
                  <span className="text-[9px] text-muted-foreground">{time}</span>
                </div>
                <p className="text-xs text-foreground/90 mt-0.5 break-words whitespace-pre-wrap">{renderContent(msg.content)}</p>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Autocomplete popups */}
      <div className="relative">
        {/* @mention autocomplete */}
        {showMentions && mentionSuggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mx-2 mb-1 bg-card border border-border rounded-lg shadow-xl z-50 py-1 max-h-48 overflow-y-auto">
            {mentionSuggestions.map((agent) => {
              const zone = ZONE_CONFIG[agent.zone];
              const st = STATUS_CONFIG[agent.status];
              return (
                <button
                  key={agent.agent_id}
                  onClick={() => insertMention(agent)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/70 transition-colors text-left"
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[7px] font-bold ${zone?.bg || "bg-muted"} ${zone?.color || "text-muted-foreground"}`}>
                    {agent.display_name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-[11px] font-medium text-foreground">{agent.display_name}</span>
                    <span className={`text-[9px] ml-1.5 ${st?.color || "text-muted-foreground"}`}>{st?.label || agent.status}</span>
                  </div>
                  <span className={`text-[8px] ${zone?.color || "text-muted-foreground"}`}>{zone?.label || agent.zone}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* /command autocomplete */}
        {showCommands && commandSuggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mx-2 mb-1 bg-card border border-border rounded-lg shadow-xl z-50 py-1">
            {commandSuggestions.map((cmd) => (
              <button
                key={cmd.cmd}
                onClick={() => insertCommand(cmd.cmd)}
                className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-secondary/70 transition-colors text-left"
              >
                <span className="text-[11px] font-mono font-medium text-primary">{cmd.cmd}</span>
                <span className="text-[10px] text-muted-foreground flex-1">{cmd.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="p-2 border-t border-border">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !showMentions && !showCommands) handleSend();
              if (e.key === "Escape") { setShowMentions(false); setShowCommands(false); }
            }}
            placeholder={`Mensagem em #${channel}... (@ para mencionar, / para comandos)`}
            className="flex-1 bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 disabled:opacity-30 transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stats Bar Component ────────────────────────────────────────

function OfficeStatsBar({ stats, zones }: { stats: OfficeAgent[]; zones: Record<string, OfficeZone> }) {
  const totalAgents = stats.length;
  const activeAgents = stats.filter((a) => ["working", "thinking", "speaking"].includes(a.status)).length;
  const errorAgents = stats.filter((a) => a.status === "error").length;
  const totalTasks = stats.reduce((sum, a) => sum + a.tasks_completed_today, 0);

  return (
    <div className="flex items-center gap-4 px-4 py-2 bg-card/50 border-b border-border">
      <div className="flex items-center gap-1.5">
        <Users className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{totalAgents} agentes</span>
      </div>
      <div className="flex items-center gap-1.5">
        <Activity className="w-3.5 h-3.5 text-primary" />
        <span className="text-xs text-primary font-medium">{activeAgents} ativos</span>
      </div>
      {errorAgents > 0 && (
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
          <span className="text-xs text-red-400">{errorAgents} erro{errorAgents > 1 ? "s" : ""}</span>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
        <span className="text-xs text-muted-foreground">{totalTasks} tarefas hoje</span>
      </div>
      <div className="ml-auto flex items-center gap-3">
        {Object.entries(zones).map(([name, zone]) => {
          const cfg = ZONE_CONFIG[name];
          if (!cfg) return null;
          return (
            <div key={name} className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${zone.active > 0 ? cfg.color.replace("text-", "bg-") + " animate-pulse" : "bg-muted-foreground/30"}`} />
              <span className="text-[10px] text-muted-foreground">{cfg.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main OfficeView Component (embeddable in Brain) ────────────

export interface OfficeViewProps {
  officeState: {
    agents: Record<string, OfficeAgent>;
    zones: Record<string, OfficeZone>;
    chat: OfficeChatMessage[];
    stats: { total_agents: number; by_status: Record<string, number>; total_tasks_today: number; total_chat_messages: number };
  } | null;
  connectionState: string;
  isConnected: boolean;
  sendChat: (content: string, channel: string) => void;
  requestState: () => void;
  executeAgent: (agentId: string, task: string) => void;
  executingAgents: Record<string, boolean>;
  agentExecEvents: Record<string, AgentExecEvent[]>;
}

export function OfficeView({
  officeState, connectionState, isConnected, sendChat, requestState,
  executeAgent, executingAgents, agentExecEvents,
}: OfficeViewProps) {
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [showChat, setShowChat] = useState(true);

  const agentsByZone = useMemo(() => {
    if (!officeState) return {};
    const groups: Record<string, OfficeAgent[]> = {};
    for (const agent of Object.values(officeState.agents)) {
      const zone = agent.zone || "unassigned";
      if (!groups[zone]) groups[zone] = [];
      groups[zone].push(agent);
    }
    const order = ["executive", "marketing", "content", "engineering", "operations", "quality", "unassigned"];
    const sorted: Record<string, OfficeAgent[]> = {};
    for (const z of order) {
      if (groups[z]) sorted[z] = groups[z];
    }
    return sorted;
  }, [officeState]);

  const selectedAgentData = officeState && selectedAgent ? officeState.agents[selectedAgent] : null;
  const allAgents = officeState ? Object.values(officeState.agents) : [];

  if (!officeState) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground">Conectando ao Escritório</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {connectionState === "connecting" && "Estabelecendo conexão..."}
            {connectionState === "reconnecting" && "Reconectando..."}
            {connectionState === "disconnected" && "Carregando via API..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Stats Bar */}
      <OfficeStatsBar stats={allAgents} zones={officeState.zones} />

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        {/* Office Floor Plan */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {Object.entries(agentsByZone).map(([zoneName, agents]) => (
              <ZoneSection
                key={zoneName}
                zoneName={zoneName}
                agents={agents}
                selectedAgent={selectedAgent}
                onSelectAgent={setSelectedAgent}
              />
            ))}
          </div>
          {selectedAgentData && (
            <div className="mt-4 max-w-lg">
              <AgentDetailPanel
                agent={selectedAgentData}
                onClose={() => setSelectedAgent(null)}
                onExecute={(task) => executeAgent(selectedAgent!, task)}
                isExecuting={!!executingAgents[selectedAgent!]}
                execEvents={agentExecEvents[selectedAgent!] || []}
              />
            </div>
          )}
        </div>

        {/* Chat Sidebar */}
        {showChat && (
          <div className="w-80 border-l border-border flex flex-col min-h-0">
            <OfficeChatPanel
              messages={officeState.chat}
              onSend={sendChat}
              agents={officeState.agents}
              onExecuteAgent={executeAgent}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export { ZONE_CONFIG, STATUS_CONFIG };
export type { OfficeViewProps as OfficeState };
